
<template>
  <div class="filter-number">
    <base-select class="filter-number__select"
                 :options="options"
                 v-model="operator"
                 borderLess
                 alignLeft
                 :titleStyle="titleStyle"/>
    <div class="filter-number__input-container">
      <input class="filter-number__input" type="text"
             v-digitsOnly
             placeholder="Enter number"
             v-model="inputModel"
             @keyup.enter="apply()"
             :class="{'filter-number__input_error': isInputError}">
      <div class="filter-number__clear-button"
           @click.stop="clearInput"
           v-if="inputModel"></div>
      <base-material-design-icon class="filter-number__search-button" icon="search" v-else/>
    </div>
  </div>
</template>

<script>
  import BaseMultiSelectSortableHeader from "./BaseMultiSelectSortableHeader";

/**
 * component is used in {@link BaseMultiSelectSortableHeader} as filter for number values like kw, msv
 */
export default {
  name: 'BaseNumberFilterDropdown',
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  props: {
     /**
     * modelValue reflects whether something is applied in this component or not to change style of parent object
     * */
    modelValue: {
      default: false
    },
    /**
     * this prop makes cross icon as a clear filter, default - clear input
     */
    clearOnCross: {
      type: Boolean,
      default: false
    },
    /**
     * this prop for options assigned outside component
     * selected options is array of selected ids
     * */
    predefinedFilter: {
      type: Object,
      default: () => ({
        operator: null,
        value: null
      })
    }
  },
  data () {
    return {
      /**
       * text entered by user
       * */
      inputModel: this.predefinedFilter.value,
      /**
       * applied data
       * */
      appliedData: { operator: this.predefinedFilter.operator, inputModel: this.predefinedFilter.value },
      /**
       * selected operator - it will be one of {@link #options}
       * */
      operator: this.predefinedFilter.operator,
      /**
       * is validating mode enabled
       * */
      isValidating: false,
      /**
       * set of available operators
       * */
      options: [
        {
          label: 'Choose option',
          value: null
        }, {
          label: 'Equal to',
          value: 'equal'
        }, {
          label: 'Not Equal',
          value: 'ne'
        }, {
          label: 'Greater Than',
          value: 'gt'
        }, {
          label: 'Less Than',
          value: 'lt'
        }, {
          label: 'Less Than/Equal to',
          value: 'lte'
        }, {
          label: 'Greater Than/Equal to',
          value: 'gte'
        }
      ]
    }
  },
  watch: {
    predefinedFilter: {
      handler (filter) {
        if (!filter) {
          return
        }
        this.inputModel = filter.value
        this.appliedData = { operator: filter.operator, inputModel: filter.value }
        this.$emit('change', this.somethingIsApplied())
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    cancel () {
      this.inputModel = ''
      this.operator = null
      this.appliedData = { operator: null, inputModel: null }
      this.$emit('change', this.somethingIsApplied())
    },
    apply () {
      if (this.oneFieldInvalid) {
        this.isValidating = true
        return
      }
      this.$emit('apply', { value: this.inputModel, operator: this.operator })
      this.appliedData.inputModel = this.inputModel
      this.appliedData.operator = this.operator
    },
    clearInput () {
      this.inputModel = ''
      if (this.clearOnCross) {
        this.operator = null
        this.apply()
      }
    },
     /**
     * invoked from parent component when component is opened/closed
     * @param isOpened
     */
    reset (isOpened) {
      if (isOpened) {
        this.inputModel = this.appliedData.inputModel
        this.operator = this.appliedData.operator
      } else {
        this.isValidating = false
        this.$emit('change', this.somethingIsApplied())
      }
    },
    somethingIsApplied () {
      return Boolean(this.appliedData.inputModel && this.appliedData.operator)
    }
  },
  computed: {
    /**
     * changes title style in validating mode
     * @returns {*}
     */
    titleStyle () {
      const commonStyle = { 'font-size': '11px', 'padding-left': '0' }
      return this.isValidating && this.oneFieldInvalid && !this.operator
        ? { ...commonStyle, color: '#e11c21' }
        : commonStyle
    },
    oneFieldInvalid () {
      return Boolean(Boolean(this.inputModel) ^ Boolean(this.operator))
    },
    isInputError () {
      return this.isValidating && !this.inputModel && this.oneFieldInvalid
    }
  }
}
</script>

<style scoped lang="scss">
  .filter-number {
    position: relative;
    flex: 1 1 100%;
    height: 100%;
    margin-left: 10px;

    &__select {
      font-size: 10px;
      font-weight: normal;
      margin: 0 5px;
    }
    &__select-title {
      margin: 0 5px;
      font-size: 11px;
      font-weight: bold;
    }

    &__clear-button,
    &__search-button {
      position: absolute;
      top: 12px;
      right: 8px;
      width: 16px;
      height: 16px;
      background-color: transparent;
      color: $rfx-dark-march;
    }

    &__clear-button {
      cursor: pointer;
      &:hover {
        opacity: 1;
      }

      &:before, &:after {
        position: absolute;
        left: 7px;
        top: 3px;
        content: '';
        height: 12px;
        width: 2px;
        background-color: currentColor;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }

    &__input-container {
      position: relative;
      padding: 10px 10px 10px 5px;
    }

    &__sort-container {
      flex: 0 0 14px;
      justify-content: center;
      display: flex;
      align-items: center;
      text-align: center;
      padding-top: 5px;
    }

    &__input {
      height: 20px;
      width: 100%;
      outline: none;
      border: none;
      padding-bottom: 10px;
      border-bottom: 1px solid $rfx-bright-blue;

      &::placeholder{
        color: $rfx-steel;
      }

      &_error {
        border-color: $rfx-red;

         &::placeholder{
           color: $rfx-red;
         }
      }
    }
  }

</style>
